import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';

// Google access token zwykle wygasa po ~60 min.
// Odświeżamy go automatycznie przed wygaśnięciem, bez wymuszonego autowylogowania.
const GOOGLE_TOKEN_REFRESH_AFTER_MS = 50 * 60 * 1000; // Odświeżenie po 50 min
const GOOGLE_TOKEN_REFRESH_RETRY_MS = 2 * 60 * 1000; // Ponów próbę po 2 min przy błędzie
const LS_GOOGLE_TOKEN_OBTAINED_AT = 'google_token_obtained_at_ms';

// Automatyczne wylogowanie po bezczynności
const IDLE_WARN_AFTER_MS = 14 * 60 * 1000; // Monit po 14 min bezczynności
const IDLE_LOGOUT_AFTER_MS = 15 * 60 * 1000; // Automatyczne wylogowanie po 15 min bezczynności

// Interfejs dla danych użytkownika
interface User {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  telefon?: string;
  position?: string;
  role: string;
  permissions?: Record<string, boolean> | null;
  is_active: boolean;
  last_login?: string;
  created_at?: string;
  preferred_tasklist_ids?: string[] | null;
  preferred_calendar_ids?: string[] | null;
  google_id?: string | null; // ID Google użytkownika (jeśli loguje się przez Google)
}

// Interfejs dla kontekstu uwierzytelniania
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => void;
  logout: (reason?: string) => void;
  error: string | null;
  googleLogoutCountdown: number | null; // Czas w sekundach do automatycznego wylogowania Google token
  idleLogoutCountdown: number | null; // Czas w sekundach do automatycznego wylogowania z powodu bezczynności
  resetIdleTimer: () => void; // Funkcja do resetowania timera bezczynności
  refreshGoogleAccessToken?: () => Promise<boolean>; // Odświeżenie tokenu Google (przed długimi operacjami Drive)
  /** Ciche odświeżenie danych użytkownika (np. saldo urlopu po wniosku) */
  refreshUser: () => Promise<void>;
}

// Utworzenie kontekstu uwierzytelniania
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook do używania kontekstu uwierzytelniania
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth musi być używany wewnątrz AuthProvider');
  }
  return context;
};

// Dostawca kontekstu uwierzytelniania
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLocalRuntime = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenExpMs, setTokenExpMs] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [countdownSec, setCountdownSec] = useState<number>(0);
  const [googleTokenObtainedAtMs, setGoogleTokenObtainedAtMs] = useState<number | null>(null);
  const [googleLogoutCountdown, setGoogleLogoutCountdown] = useState<number | null>(null);
  const [lastActivityTime, setLastActivityTime] = useState<number | null>(null);
  const [idleWarningModalOpen, setIdleWarningModalOpen] = useState<boolean>(false);
  const [idleLogoutCountdown, setIdleLogoutCountdown] = useState<number | null>(null);
  const logoutTimeoutRef = useRef<number | null>(null);
  const warnTimeoutRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const googleRefreshTimeoutRef = useRef<number | null>(null);
  const idleWarnTimeoutRef = useRef<number | null>(null);
  const idleLogoutTimeoutRef = useRef<number | null>(null);
  const idleCountdownIntervalRef = useRef<number | null>(null);
  const idleCheckIntervalRef = useRef<number | null>(null);
  const idleLogoutAtRef = useRef<number | null>(null); // Czas wylogowania (timestamp)

  // Sprawdzenie, czy użytkownik jest uwierzytelniony
  const isAuthenticated = !!user;

  // Funkcja do logowania
  const login = (token: string) => {
    localStorage.setItem('auth_token', token);
    // Start "sesji Google" – zakładamy, że login przez Google oznacza uzyskanie świeżego access tokenu.
    const now = Date.now();
    console.log('[Google Token] 🔐 Nowe logowanie - ustawiam timestamp:', new Date(now).toLocaleTimeString('pl-PL'));
    localStorage.setItem(LS_GOOGLE_TOKEN_OBTAINED_AT, String(now));
    setGoogleTokenObtainedAtMs(now);
    setLastActivityTime(now);
    try {
      const expMs = decodeJwtExpMs(token);
      setTokenExpMs(expMs);
    } catch {}
    fetchUser();
  };

  // Funkcja do wylogowywania
  const logout = useCallback(async (reason?: string) => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Błąd podczas wylogowywania:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem(LS_GOOGLE_TOKEN_OBTAINED_AT);
      setUser(null);
      setTokenExpMs(null);
      setModalOpen(false);
      setGoogleTokenObtainedAtMs(null);
      setGoogleLogoutCountdown(null);
      setLastActivityTime(null);
      setIdleLogoutCountdown(null);
      clearJwtTimers();
      clearGoogleTimers();
      clearIdleTimers();
      // Przekierowanie na stronę logowania po wylogowaniu z przyczyną jeśli podana
      const loginUrl = reason ? `/login?reason=${encodeURIComponent(reason)}` : '/login';
      window.location.href = loginUrl; 
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const response = await api.get('/auth/me', { suppressAuthErrorLog: true } as any);
      if (response.data.success) setUser(response.data.data);
    } catch {
      // cicho — np. sesja wygasła; nie przerywamy pracy użytkownika
    }
  }, []);

  // Funkcja do pobierania danych użytkownika
  const fetchUser = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Ustawienie tokenu w nagłówku Authorization
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Zdekoduj exp i ustaw w stanie, jeśli jeszcze nie ustawione
      try {
        const expMs = decodeJwtExpMs(token);
        setTokenExpMs(expMs);
      } catch {}
      
      // Pobranie danych użytkownika (bez głośnych logów 401)
      const response = await api.get('/auth/me', { suppressAuthErrorLog: true } as any);
      
      if (response.data.success) {
        const u = response.data.data;
        setUser(u);

        // Jeśli użytkownik loguje się przez Google (ma google_id), ustaw timestamp dla automatycznego odświeżania.
        // Uwaga: google_access_token nie jest zwracany z backendu ze względów bezpieczeństwa,
        // więc sprawdzamy google_id jako wskaźnik, że użytkownik używa Google OAuth.
        try {
          const hasGoogleId = !!(u as any)?.google_id;
          console.log('[Google Token] 👤 fetchUser - użytkownik ma google_id:', hasGoogleId);
          
          if (hasGoogleId) {
            const stored = localStorage.getItem(LS_GOOGLE_TOKEN_OBTAINED_AT);
            const ms = stored ? Number(stored) : NaN;
            console.log('[Google Token] 📦 Timestamp w localStorage:', stored ? new Date(ms).toLocaleTimeString('pl-PL') : 'BRAK');
            
            if (!stored || !Number.isFinite(ms) || ms <= 0) {
              const now = Date.now();
              console.log('[Google Token] ✨ Ustawiam nowy timestamp:', new Date(now).toLocaleTimeString('pl-PL'));
              localStorage.setItem(LS_GOOGLE_TOKEN_OBTAINED_AT, String(now));
              setGoogleTokenObtainedAtMs(now);
            } else {
              console.log('[Google Token] ✅ Używam istniejącego timestamp:', new Date(ms).toLocaleTimeString('pl-PL'));
              setGoogleTokenObtainedAtMs(ms);
            }
          } else {
            // użytkownik bez integracji Google – nie pokazuj licznika
            console.log('[Google Token] ⚠️ Użytkownik nie ma google_id - czyszczę timestamp');
            localStorage.removeItem(LS_GOOGLE_TOKEN_OBTAINED_AT);
            setGoogleTokenObtainedAtMs(null);
            clearGoogleTimers();
          }
        } catch (e) {
          console.error('[Google Token] ❌ Błąd przy ustawianiu timestamp:', e);
        }
      } else {
        setError(response.data.message || 'Błąd podczas pobierania danych użytkownika');
        localStorage.removeItem('auth_token');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        // Ciche czyszczenie tokenu na publicznych stronach / przy wygaśnięciu
        localStorage.removeItem('auth_token');
        delete api.defaults.headers.common['Authorization'];
      } else {
        console.error('Błąd podczas pobierania danych użytkownika:', error);
        setError('Błąd podczas pobierania danych użytkownika');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Pomocnicze: dekodowanie exp z JWT
  const decodeJwtExpMs = (token: string): number => {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    const payload = JSON.parse(atob(base64UrlToBase64(parts[1])));
    if (!payload.exp) throw new Error('No exp in JWT');
    return payload.exp * 1000;
  };

  const base64UrlToBase64 = (str: string): string => {
    return str.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(str.length / 4) * 4, '=');
  };

  // Czyść timery/intervale JWT (sesja aplikacji)
  const clearJwtTimers = () => {
    if (logoutTimeoutRef.current) {
      window.clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (warnTimeoutRef.current) {
      window.clearTimeout(warnTimeoutRef.current);
      warnTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  // Czyść timery/intervale Google access token
  const clearGoogleTimers = () => {
    if (googleRefreshTimeoutRef.current) {
      window.clearTimeout(googleRefreshTimeoutRef.current);
      googleRefreshTimeoutRef.current = null;
    }
    setGoogleLogoutCountdown(null);
  };
  // Odśwież Google access token na backendzie i zresetuj lokalny licznik czasu jego uzyskania.
  const refreshGoogleAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      await api.post('/auth/google/refresh');
      const now = Date.now();
      localStorage.setItem(LS_GOOGLE_TOKEN_OBTAINED_AT, String(now));
      setGoogleTokenObtainedAtMs(now);
      setGoogleLogoutCountdown(null);
      console.log('[Google Token] ✅ Access token odświeżony automatycznie');
      return true;
    } catch (e) {
      console.error('[Google Token] ❌ Nie udało się automatycznie odświeżyć access tokenu:', e);
      return false;
    }
  }, []);


  // Czyść timery/intervale bezczynności
  const clearIdleTimers = () => {
    if (idleWarnTimeoutRef.current) {
      window.clearTimeout(idleWarnTimeoutRef.current);
      idleWarnTimeoutRef.current = null;
    }
    if (idleLogoutTimeoutRef.current) {
      window.clearTimeout(idleLogoutTimeoutRef.current);
      idleLogoutTimeoutRef.current = null;
    }
    if (idleCountdownIntervalRef.current) {
      window.clearInterval(idleCountdownIntervalRef.current);
      idleCountdownIntervalRef.current = null;
    }
    if (idleCheckIntervalRef.current) {
      window.clearInterval(idleCheckIntervalRef.current);
      idleCheckIntervalRef.current = null;
    }
    setIdleLogoutCountdown(null);
    idleLogoutAtRef.current = null;
  };

  // Funkcja do resetowania czasu bezczynności
  const resetIdleTimer = useCallback(() => {
    if (!isAuthenticated) return;
    
    const now = Date.now();
    setLastActivityTime(now);
    clearIdleTimers();

    // Zapamiętaj czas wylogowania (od momentu rozpoczęcia bezczynności)
    const logoutAt = now + IDLE_LOGOUT_AFTER_MS;
    idleLogoutAtRef.current = logoutAt;

    // Ustaw timeout na pokazanie licznika po 14 minutach
    idleWarnTimeoutRef.current = window.setTimeout(() => {
      console.log('[Idle Timer] ⚠️ Pokazuję licznik wylogowania z powodu bezczynności');
      
      // Uruchom licznik czasu
      const updateIdleCountdown = () => {
        if (!idleLogoutAtRef.current) {
          setIdleLogoutCountdown(null);
          return;
        }
        
        const currentTime = Date.now();
        const msUntilLogout = idleLogoutAtRef.current - currentTime;
        const secondsUntilLogout = Math.max(0, Math.floor(msUntilLogout / 1000));
        
        if (secondsUntilLogout > 0) {
          setIdleLogoutCountdown(secondsUntilLogout);
        } else {
          setIdleLogoutCountdown(null);
        }
      };
      
      updateIdleCountdown();
      idleCountdownIntervalRef.current = window.setInterval(updateIdleCountdown, 1000);
      
      // Ustaw timeout na automatyczne wylogowanie za 1 minutę od pokazania licznika (15 min - 14 min)
      idleLogoutTimeoutRef.current = window.setTimeout(() => {
        console.log('[Idle Timer] ⏰ Automatyczne wylogowanie z powodu bezczynności');
        logout('idle');
      }, IDLE_LOGOUT_AFTER_MS - IDLE_WARN_AFTER_MS);
    }, IDLE_WARN_AFTER_MS);
  }, [isAuthenticated, logout]);

  // Funkcja do odświeżania tokenu
  const handleStayLoggedIn = useCallback(async () => {
    try {
      const res = await api.post('/auth/refresh');
      const newToken = res.data?.token;
      if (!newToken) throw new Error('Brak nowego tokenu');
      localStorage.setItem('auth_token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      const expMs = decodeJwtExpMs(newToken);
      setTokenExpMs(expMs);
      setModalOpen(false);
      // Odświeżono dostęp Google – zresetuj licznik
      try {
        const now = Date.now();
        localStorage.setItem(LS_GOOGLE_TOKEN_OBTAINED_AT, String(now));
        setGoogleTokenObtainedAtMs(now);
        setGoogleLogoutCountdown(null);
        // setLastActivityTime(now); // WYŁĄCZONE: Autowylogowanie po bezczynności jest wyłączone
        clearGoogleTimers();
        // resetIdleTimer(); // WYŁĄCZONE: Autowylogowanie po bezczynności jest wyłączone
      } catch {}
      // Odśwież dane użytkownika opcjonalnie
      // await fetchUser(); // niekonieczne, jeśli payload nie zmienia uprawnień
      return true;
    } catch (e) {
      console.error('Nie udało się odświeżyć tokenu:', e);
      // Jeśli refresh się nie uda – wyloguj tylko jeśli to było ręczne odświeżanie (nie automatyczne)
      if (modalOpen) {
        logout();
      }
      return false;
    }
  }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Harmonogram ostrzeżenia i auto‑wylogowania
  useEffect(() => {
    clearJwtTimers();
    if (!tokenExpMs) return;
    const now = Date.now();
    const msToExp = tokenExpMs - now;
    const warnMs = 2 * 60 * 1000; // 2 minuty

    if (msToExp <= 0) {
      logout();
      return;
    }

    // Ustaw timeout auto‑wylogowania o czasie wygaśnięcia
    logoutTimeoutRef.current = window.setTimeout(() => {
      logout();
    }, msToExp);

    // Jeśli mniej niż 2 min do końca – pokaż modal teraz, inaczej ustaw czas ostrzeżenia
    if (msToExp <= warnMs) {
      openWarningModal(tokenExpMs);
    } else {
      warnTimeoutRef.current = window.setTimeout(() => {
        openWarningModal(tokenExpMs);
      }, msToExp - warnMs);
    }
  }, [tokenExpMs]);

  const openWarningModal = (expMsValue: number) => {
    setModalOpen(true);
    // Ustaw od razu pierwszy stan odliczania
    updateCountdown(expMsValue);
    countdownIntervalRef.current = window.setInterval(() => updateCountdown(expMsValue), 1000);
  };

  const updateCountdown = (expMsValue: number) => {
    const now = Date.now();
    const sec = Math.max(0, Math.floor((expMsValue - now) / 1000));
    setCountdownSec(sec);
  };

  // Harmonogram automatycznego odświeżania Google access token (bez autowylogowania).
  useEffect(() => {
    console.log('[Google Token] 🔍 useEffect uruchomiony:', { isAuthenticated, googleTokenObtainedAtMs });
    clearGoogleTimers();

    // Zabezpieczenie: na lokalnym frontendzie nie odświeżamy tokenu Google,
    // żeby nie ingerować w sesję produkcyjną przy przypadkowym API pointing na prod.
    if (isLocalRuntime) {
      console.log('[Google Token] 🛑 Local runtime - pomijam automatyczne odświeżanie tokenu Google');
      return;
    }

    if (!isAuthenticated) {
      console.log('[Google Token] ⚠️ Nieautoryzowany - pomijam ustawienie timera');
      return;
    }
    if (!googleTokenObtainedAtMs) {
      console.log('[Google Token] ⚠️ Brak googleTokenObtainedAtMs - pomijam ustawienie timera');
      return;
    }

    // TypeScript guard - po sprawdzeniu powyżej googleTokenObtainedAtMs nie może być null
    const tokenObtainedAt = googleTokenObtainedAtMs;
    const now = Date.now();
    const refreshAt = tokenObtainedAt + GOOGLE_TOKEN_REFRESH_AFTER_MS;
    const msUntilRefresh = refreshAt - now;

    console.log('[Google Token] 📊 Dane timera:', {
      googleTokenObtainedAtMs: new Date(tokenObtainedAt).toLocaleTimeString('pl-PL'),
      refreshAt: new Date(refreshAt).toLocaleTimeString('pl-PL'),
      now: new Date(now).toLocaleTimeString('pl-PL'),
      msUntilRefresh: Math.floor(msUntilRefresh / 1000)
    });

    const scheduleRefreshAttempt = (delayMs: number) => {
      googleRefreshTimeoutRef.current = window.setTimeout(async () => {
        const refreshed = await refreshGoogleAccessToken();
        if (!refreshed) {
          // Brak autowylogowania - ponawiamy odświeżenie po krótkim czasie.
          scheduleRefreshAttempt(GOOGLE_TOKEN_REFRESH_RETRY_MS);
        }
      }, Math.max(0, delayMs));
    };

    if (msUntilRefresh <= 0) {
      console.log('[Google Token] ⚠️ Czas odświeżenia minął - wykonuję odświeżenie natychmiast');
      scheduleRefreshAttempt(0);
    } else {
      console.log(`[Google Token] ⏰ Zaplanowano odświeżenie za ${Math.floor(msUntilRefresh / 1000)} sekund (${Math.floor(msUntilRefresh / 60000)} min)`);
      scheduleRefreshAttempt(msUntilRefresh);
    }
  }, [isAuthenticated, googleTokenObtainedAtMs, refreshGoogleAccessToken, isLocalRuntime]);

  // Inicjalizacja timera bezczynności przy logowaniu
  // WYŁĄCZONE: Autowylogowanie po bezczynności jest wyłączone
  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     return;
  //   }

  //   // Inicjalizuj timer bezczynności
  //   resetIdleTimer();

  //   return () => {
  //     clearIdleTimers();
  //   };
  // }, [isAuthenticated, resetIdleTimer]);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      setError(null);
      
      // Sprawdzanie tokenu w URL (po przekierowaniu z Google)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');

      if (tokenFromUrl) {
        // Usunięcie tokenu z URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        // Zaloguj użytkownika używając tokenu z URL
        login(tokenFromUrl); 
      } else {
        // Jeśli nie ma tokenu w URL, sprawdź localStorage
        const tokenFromStorage = localStorage.getItem('auth_token');
        if (tokenFromStorage) {
          // Jeśli token wygasł – od razu wyloguj
          try {
            const expMs = decodeJwtExpMs(tokenFromStorage);
            if (expMs <= Date.now()) {
              logout();
              return;
            }
            setTokenExpMs(expMs);
          } catch {}
          // Przy odświeżeniu strony spróbuj odtworzyć timestamp Google tokenu
          try {
            const stored = localStorage.getItem(LS_GOOGLE_TOKEN_OBTAINED_AT);
            const ms = stored ? Number(stored) : NaN;
            if (stored && Number.isFinite(ms) && ms > 0) {
              console.log('[Google Token] 📥 Wczytano timestamp z localStorage:', new Date(ms).toLocaleTimeString('pl-PL'));
              setGoogleTokenObtainedAtMs(ms);
            } else {
              console.log('[Google Token] ⚠️ Brak timestamp w localStorage - ustawiam nowy');
              const now = Date.now();
              localStorage.setItem(LS_GOOGLE_TOKEN_OBTAINED_AT, String(now));
              setGoogleTokenObtainedAtMs(now);
            }
          } catch (e) {
            console.warn('[Google Token] ⚠️ Błąd przy wczytywaniu timestamp:', e);
            const now = Date.now();
            localStorage.setItem(LS_GOOGLE_TOKEN_OBTAINED_AT, String(now));
            setGoogleTokenObtainedAtMs(now);
          }
          // Jeśli jest token w localStorage, pobierz dane użytkownika
          await fetchUser();
        } else {
          // Jeśli nie ma żadnego tokenu, zakończ ładowanie
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
  }, []); // Pusta tablica zależności zapewnia, że efekt uruchomi się tylko raz po zamontowaniu komponentu

  // Wartość kontekstu
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    error,
    googleLogoutCountdown,
    idleLogoutCountdown,
    resetIdleTimer,
    refreshGoogleAccessToken,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Dialog open={modalOpen} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle>Sesja wygaśnie wkrótce</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography>Użytkownik zostanie wylogowany za</Typography>
            <Typography variant="h5" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCountdown(countdownSec)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleStayLoggedIn}>
            Pozostań w Unitresco
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};

export default AuthContext;

// Format MM:SS
function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return `${mm}:${ss}`;
}
